// src/hooks/usePublicStats.js
import { useEffect, useRef, useState } from "react";

const TTL_MS = 5 * 60 * 1000; // 5 min välimuisti

// CRA-ympäristömuuttujat
const NODE_ENV   = process.env.NODE_ENV; // 'development' | 'production' | 'test'
const USE_LOCAL  = String(process.env.REACT_APP_USE_LOCAL_API || "").toLowerCase() === "true";
const API_BASE   = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");       // esim. https://api.buukkeri.fi
const LOCAL_BASE = (process.env.REACT_APP_LOCAL_API_BASE || "").replace(/\/+$/, ""); // esim. http://127.0.0.1:8080
const API_PREFIX = (process.env.REACT_APP_API_PREFIX || "/api").replace(/\/+$/, ""); // esim. /api

function uniq(arr) {
  return Array.from(new Set(arr.filter(Boolean)));
}

function buildCandidateUrls() {
  const origin = (typeof window !== "undefined" ? window.location.origin : "").replace(/\/+$/, "");
  const list = [];

  if (NODE_ENV === "development") {
    // 1) CRA dev-proxy same-origin reitti (/api/…)
    if (origin) list.push(`${origin}${API_PREFIX}/public/stats`);
    // 2) Erillinen local-backend (jos ei käytä proxyä)
    if (USE_LOCAL && LOCAL_BASE) list.push(`${LOCAL_BASE}${API_PREFIX}/public/stats`);
    // 3) Varalla prod-API
    if (API_BASE) list.push(`${API_BASE}${API_PREFIX}/public/stats`);
  } else {
    // production
    // 1) Prod-API (suositus)
    if (API_BASE) list.push(`${API_BASE}${API_PREFIX}/public/stats`);
    // 2) Same-origin /api (reverse-proxy tuotannossa)
    if (origin) list.push(`${origin}${API_PREFIX}/public/stats`);
  }

  return uniq(list);
}

async function fetchWithTimeout(url, opts = {}, ms = 8000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { ...opts, signal: ctrl.signal });
    return res;
  } finally {
    clearTimeout(t);
  }
}

export default function usePublicStats() {
  const [data, setData] = useState({ bookings_total: 0, quotes_total: 0, artists_total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // StrictMode-tuplakäynnistyksen esto ja intervallin hallinta
  const didInitRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (didInitRef.current) return; // estä tuplasuoritus dev-StrictModessa
    didInitRef.current = true;

    let alive = true;
    const cacheKey = "public.stats.cache.v2"; // bustaa mahdolliset nolla-cachet

    // --- Välimuisti (sessionStorage) ---
    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === "object" && Number(cached.ts) > 0) {
          const fresh = Date.now() - cached.ts < TTL_MS;
          if (fresh) {
            if (!alive) return;
            setData({
              bookings_total: Number(cached.data?.bookings_total) || 0,
              quotes_total:   Number(cached.data?.quotes_total)   || 0,
              artists_total:  Number(cached.data?.artists_total)  || 0,
            });
            setLoading(false);
          }
        }
      }
    } catch {
      /* ei kriittinen */
    }

    const candidates = buildCandidateUrls();
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    const load = async () => {
      let lastErr = null;

      for (const url of candidates) {
        try {
          const sameOrigin = origin && url.startsWith(origin);
          const res = await fetchWithTimeout(
            url,
            {
              method: "GET",
              headers: { Accept: "application/json" },
              cache: "no-store",
              ...(sameOrigin ? { credentials: "same-origin" } : {}),
            },
            8000
          );

          if (!res.ok) {
            lastErr = new Error(`HTTP ${res.status} ${res.statusText} for ${url}`);
            continue;
          }

          let json;
          try {
            json = await res.json();
          } catch {
            lastErr = new Error(`JSON parse failed for ${url}`);
            continue;
          }

          const payload = json && json.success && json.data ? json.data : json;
          const clean = {
            bookings_total: Number(payload?.bookings_total) || 0,
            quotes_total:   Number(payload?.quotes_total)   || 0,
            artists_total:  Number(payload?.artists_total)  || 0,
          };

          if (!alive) return;
          setData(clean);
          setLoading(false);
          setError(null);

          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: clean }));
          } catch {}

          return; // onnistui → lopeta
        } catch (e) {
          lastErr = e;
          // kokeile seuraavaa kandidaattia
        }
      }

      if (!alive) return;
      setError(lastErr || new Error("Failed to load stats"));
      setLoading(false);
      // älä heitä, vain logita
      console.error("[usePublicStats] all candidates failed:", candidates, lastErr);
    };

    // Lataa heti (cache estää turhan verkon)
    load();

    // Refetch kun välilehti aktivoidaan ja cache on vanhentunut
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
        const fresh = cached && Date.now() - Number(cached.ts) < TTL_MS;
        if (!fresh) load();
      } catch {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Turvaintervalli: pingaa TTL-välein (jos välilehti on aktiivinen ja cache vanha)
    timerRef.current = setInterval(onVisibility, TTL_MS);

    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []); // init vain kerran

  return { data, loading, error };
}
